import { Base64 } from 'js-base64';
import Quill from 'quill';

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TReaction } from 'src/app/project/modules/reactions/reactions.model';
import { TUser } from 'src/app/project/modules/user/user.model';

import { TAllUsers } from '@project/view-models';
import { PipesModule } from 'src/app/core/pipes/pipes.module';
import { IconModule } from 'src/app/project/components/icon/icon-module';
import { TRichText } from 'src/app/project/components/input/rich-text/rich-text-ops.model';
import { TooltipsModule } from 'src/app/project/features/tooltip/tooltips.module';
import { UserService } from 'src/app/project/modules/user/user.service';
import { UsersService } from 'src/app/project/modules/users/users.service';
import { ReactionsService } from 'src/app/project/services/reactions.service';
import { EIconPath } from '../../../../../../shared/enums/icons.enum';

@Component({
  selector: 'pp-description-activity',
  templateUrl: './description-activity.component.html',
  styleUrls: ['./description-activity.component.scss'],
  imports: [PipesModule, TooltipsModule, IconModule],
})
export class DescriptionActivityComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('description', { static: true }) descriptionElement: ElementRef;

  @Input() ppPlainText = '';
  @Input() ppDescription: string;
  @Input() ppComment = false;
  @Input() ppId = '';
  @Input() ppPointId = '';
  @Input() ppShowEdit: boolean;
  @Output() ppEdit = new EventEmitter<void>();

  private quill: Quill;
  private richText: TRichText;
  expanded = false;
  long = false;
  length: number;
  textLines = 0;
  splitText = [];
  user: TUser;
  reaction: TReaction;
  users: TAllUsers;
  reactionTooltip = '';
  EIconPath = EIconPath;

  constructor(
    private reactionsService: ReactionsService,
    private userService: UserService,
    private usersService: UsersService,
  ) {}

  ngOnInit() {
    this.user = this.userService.getUser();
    this.users = this.usersService.getUsers();

    this.reactionsService.reactionsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setReactions();
    });
    this.setReactions();

    if (this.ppDescription) {
      try {
        this.richText = JSON.parse(Base64.decode(this.ppDescription));
      } catch {
        this.richText = { ops: [] };
      }
    }

    this.initEditor();
    this.initContent();
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  setReactions(): void {
    this.reaction = this.reactionsService
      .getReactions()
      [this.ppPointId]?.data.find((_reaction) => _reaction.targetRef.id === this.ppId);

    this.reactionTooltip = this.reactionsService.getReactionTooltip(this.reaction);
  }

  initEditor(): void {
    this.quill = new Quill(this.descriptionElement.nativeElement, {
      readOnly: true,
      modules: {
        toolbar: false,
      },
    });
  }

  initContent(): void {
    if (this.quill && this.richText) {
      this.quill.setContents(this.richText.ops);

      this.length = this.quill.getText().trim().length;
      this.splitText = this.quill.getText().split(/\r\n|\r|\n/);
      this.textLines = this.splitText.length;

      if (this.textLines > 3) {
        let lengthToTrim =
          this.splitText[0].length + this.splitText[1].length + this.splitText[2].length;

        if (lengthToTrim > 200) {
          lengthToTrim = 200;
        }

        this.long = true;
        this.quill.editor.deleteText(lengthToTrim, this.length);
        this.quill.editor.insertText(lengthToTrim, '...', 'bold', true);
      } else if (this.length > 200) {
        this.long = true;
        this.quill.editor.deleteText(200, this.length);
        this.quill.editor.insertText(200, '...', 'bold', true);
      }
    } else if (this.quill) {
      this.quill.setText(this.ppPlainText);

      this.length = this.quill.getLength();
      this.splitText = this.quill.getText().split(/\r\n|\r|\n/);
      this.textLines = this.splitText.length;

      if (this.textLines > 3) {
        let lengthToTrim =
          this.splitText[0].length + this.splitText[1].length + this.splitText[2].length;

        if (lengthToTrim > 200) {
          lengthToTrim = 200;
        }

        this.long = true;
        this.quill.editor.deleteText(lengthToTrim, this.length);
        this.quill.editor.insertText(lengthToTrim, '...', 'bold', true);
      } else if (this.length > 200) {
        this.long = true;
        this.quill.editor.deleteText(200, this.length);
        this.quill.editor.insertText(200, '...', 'bold', true);
      }
    }
  }

  expandDescription(): void {
    this.expanded = true;

    if (this.richText) {
      this.quill.setContents(this.richText.ops);
    } else if (this.ppPlainText) {
      this.quill.setText(this.ppPlainText);
    }
  }

  collapseDescription(): void {
    this.expanded = false;

    if (this.richText) {
      this.quill.setContents(this.richText.ops);
    } else if (this.ppPlainText) {
      this.quill.setText(this.ppPlainText);
    }

    if (this.textLines > 3) {
      let lengthToTrim =
        this.splitText[0].length + this.splitText[1].length + this.splitText[2].length;

      if (lengthToTrim > 200) {
        lengthToTrim = 200;
      }

      this.long = true;
      this.quill.editor.deleteText(lengthToTrim, this.length);
      this.quill.editor.insertText(lengthToTrim, '...', 'bold', true);
    } else if (this.length > 200) {
      this.long = true;
      this.quill.editor.deleteText(200, this.length);
      this.quill.editor.insertText(200, '...', 'bold', true);
    }
  }

  likeComment(): void {
    const liked = this.reaction?.like.includes(this.user.userId);

    this.reactionsService.likeComment(this.ppId, this.ppPointId, !liked).subscribe();
  }

  edit(): void {
    this.ppEdit.emit();
  }
}
